// A ride resumed after the page closed (ADR 0028): what a solo ride keeps with its recording so it can go on, and the
// ride configuration rebuilt from it. A race against bots keeps nothing: its field only lived in the page, and placing
// the player back among new bots would hand out a result nobody rode for. An online ride keeps nothing either: the room does.
import { RideSetup, type OpenRecording, type RoomWorkout } from '@gruvetto/contracts';
import { TRACKS, findChallenge, isModeId, resolveRules, MODES, type Samples } from '@gruvetto/core';
import { catalog, raceConfig } from '../../data/creations.js';
import { challengeConfig } from './challengeRide.js';

/** What the session needs of a ride's configuration (session.ts startRide). */
interface RideCfg {
  race: { mode: string; track: string; laps?: number; bots?: number; diff?: string; groupWkg?: number; elastic?: boolean; items?: string[]; itemsOff?: string[]; rules?: unknown };
  workout?: { w: RoomWorkout & Record<string, unknown>; planKey?: string | null };
  challenge?: { def: { id: string } };
  online?: unknown; spectate?: boolean; coach?: boolean; event?: unknown;
}

/** Distance ridden at the last sample the server kept (m). */
export const lastDistance = (s: Samples): number => (s.d.length ? Math.max(0, s.d[s.d.length - 1]) : 0);

/** The setup a ride keeps with its recording, or null when it could not be resumed (finish line, online, practice). */
export function setupOf(cfg: RideCfg, finish: string, intensity = 1): RideSetup | null {
  if (cfg.online || cfg.spectate || cfg.coach || cfg.event) return null;
  const rc = cfg.race;
  let setup: unknown;
  if (cfg.challenge) setup = { kind: 'challenge', id: cfg.challenge.def.id };
  else if (cfg.workout) {
    const w = cfg.workout.w;
    setup = { kind: 'workout', track: rc.track, planKey: cfg.workout.planKey ?? null, intensity, workout: { id: w.id, name: w.name, cat: w.cat, segs: w.segs, ...(w.test ? { test: w.test } : {}), ...(w.desc ? { desc: w.desc } : {}) } };
  } else if (finish === 'open') {
    const { mode, track, laps, bots, diff, groupWkg, elastic, items, itemsOff } = rc;
    setup = { kind: 'race', mode, track, laps, bots, diff, groupWkg, elastic, items, itemsOff };
  } else return null;
  const ok = RideSetup.safeParse(setup);
  return ok.success ? ok.data : null;
}

const knownTrack = (id: string) => !!TRACKS[id] || !!catalog.get(id, 'track');

/**
 * The ride configuration a setup rebuilds, or null when the ride cannot go on: a mode or a track that is no longer in
 * the catalogue, a challenge that no longer exists, a mode whose finish is not open (a setup from an older client).
 */
export function configOf(setup: RideSetup | null) {
  if (!setup) return null;
  if (setup.kind === 'challenge') {
    const def = findChallenge(setup.id);
    return def ? challengeConfig(def) : null;
  }
  if (!knownTrack(setup.track)) return null;
  if (setup.kind === 'workout') return { race: { ...raceConfig({ mode: 'race', track: setup.track }), mode: 'workout' }, workout: { w: setup.workout, planKey: setup.planKey } };
  const mode = setup.mode, entry = isModeId(mode) ? null : catalog.get(mode, 'mode');
  if (!isModeId(mode) && !entry) return null;
  const { track, laps, bots, diff, groupWkg, elastic, items, itemsOff } = setup;
  const race = raceConfig({ mode, track, laps, bots, diff, groupWkg, elastic, items, itemsOff });
  const finish = resolveRules(race.rules as never, isModeId(mode) ? MODES[mode].rules : MODES.race.rules).finish;
  return finish === 'open' ? { race } : null;
}

/** Whether an open recording can go on in a new ride, rather than only be saved or dropped. */
export const canResume = (rec: OpenRecording): boolean => !rec.meta.online && !!configOf(rec.setup);
